import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * ProtectedRoute — wraps admin-only pages. Three states:
 *   loading -> show subtle loader
 *   unauthenticated -> redirect to /admin/login
 *   authenticated but not admin -> redirect to /admin/login with a notice
 *     AND silently clear the stored session so a hard refresh of /admin
 *     doesn't keep round-tripping through /auth/me.
 */
export const ProtectedRoute = ({ children }) => {
  const { loading, isAuthenticated, isAdmin, user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!loading && isAuthenticated && !isAdmin) {
      // Fire-and-forget — by the time it resolves the redirect below has
      // already happened, so the user sees the "not authorised" notice once
      // and subsequent visits start cleanly.
      logout();
    }
  }, [loading, isAuthenticated, isAdmin, logout]);

  if (loading) {
    return (
      <div className="sp-auth-screen" data-testid="protected-loading">
        <div className="sp-auth-card">
          <div className="sp-spinner" aria-hidden="true" />
          <p className="sp-modal-sub" style={{ textAlign: "center", margin: 0 }}>
            Verifying access…
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return (
      <Navigate
        to="/admin/login"
        state={{ unauthorizedEmail: user?.email }}
        replace
      />
    );
  }

  return children;
};
